import React from "react";
import PropTypes from "prop-types";

// Minimal, dependency-free sparkline. Built as a single inline SVG because adding a
// charting lib for a 60×24px line would be wildly disproportionate to the surface area.
//
// Renders the trend of a series with the final point emphasised so the user's eye lands
// on "where are we right now?". The full y-range is mapped to the SVG height, so the
// shape carries the information — absolute magnitude is shown by the headline number in
// the KPI card, not by the sparkline.
//
// Visual rules:
//   - Stroke colour follows the *direction* of the trend by default (down = teal, up =
//     yellow) so "unallocated trending down" reads as healthy at a glance. Override with
//     the `stroke` prop for trend-neutral series.
//   - All-zero or flat series collapses to a horizontal line at mid-height — never an
//     empty SVG, so the card never has a visual hole where the chart should be.
//   - 1-point series renders just the dot.
const Sparkline = ({
  data,
  width = 64,
  height = 22,
  stroke,
  goodDirection = "down",
  className = "",
  ariaLabel,
  // When true, render as a full-width gradient-filled area (matching the SpendSparkline on
  // /financial-control) instead of a fixed-width bare line. Used by the KPI cards so their
  // trend reads as the same kind of chart as the Economic Control hero tile.
  fill = false,
}) => {
  const gradId = React.useId();
  if (!data || data.length === 0) return null;

  const values = data.map((d) => (typeof d === "number" ? d : d.value)) || [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1; // avoid /0 on a flat line — fall back to mid-height
  const padY = 2;
  const usableH = height - padY * 2;
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;

  // Normalise: y inverts so larger values sit higher in SVG coords (which are y-down).
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = padY + usableH - ((v - min) / range) * usableH;
    return [x, y];
  });

  // Direction-aware default tone — "good" when the series moves the user-preferred way.
  // For unallocated cost, down = good. For coverage %, up = good.
  let autoStroke = "hsl(var(--chart-pred))"; // neutral
  if (values.length >= 2) {
    const first = values[0];
    const last = values[values.length - 1];
    if (last < first) {
      autoStroke = goodDirection === "down" ? "hsl(var(--chart-teal))" : "hsl(var(--warning))"; // good / bad
    } else if (last > first) {
      autoStroke = goodDirection === "down" ? "hsl(var(--warning))" : "hsl(var(--chart-teal))";
    }
  }
  const effectiveStroke = stroke || autoStroke;

  const path = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [lastX, lastY] = points[points.length - 1];

  // Area path (fill mode): drop from the baseline up through every point and back down,
  // so a soft gradient sits under the actual line — the SpendSparkline recipe.
  const areaPath =
    fill && points.length > 1
      ? `M ${points[0][0].toFixed(1)} ${height} ` +
        points.map(([x, y]) => `L ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ") +
        ` L ${lastX.toFixed(1)} ${height} Z`
      : null;

  return (
    <svg
      role={ariaLabel ? "img" : "presentation"}
      aria-label={ariaLabel}
      width={fill ? "100%" : width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio={fill ? "none" : "xMidYMid meet"}
      className={fill ? `block w-full ${className}` : className}
    >
      {areaPath && (
        <>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={effectiveStroke} stopOpacity="0.22" />
              <stop offset="100%" stopColor={effectiveStroke} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
        </>
      )}
      {points.length > 1 && (
        <polyline
          fill="none"
          stroke={effectiveStroke}
          strokeWidth={fill ? 1.6 : 1.25}
          strokeLinejoin="round"
          strokeLinecap="round"
          points={path}
          vectorEffect={fill ? "non-scaling-stroke" : undefined}
        />
      )}
      {/* End-point dot only in the bare-line mode; the filled area already grounds the eye. */}
      {!fill && <circle cx={lastX} cy={lastY} r={1.75} fill={effectiveStroke} />}
    </svg>
  );
};

Sparkline.propTypes = {
  // Either a numeric array, or an array of objects with a `value` field. Objects are
  // useful when the caller already has {day, value} shaped data — keeps the data type
  // unified across producer and consumer.
  data: PropTypes.array.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  // Override the auto trend-tone. Useful when the series is neither good-when-up nor
  // good-when-down (e.g. an absolute count where direction is just context).
  stroke: PropTypes.string,
  // Which direction reads as "good" for this metric. Drives the auto colour selection.
  goodDirection: PropTypes.oneOf(["up", "down"]),
  className: PropTypes.string,
  // Optional accessible label — defaults to presentation role since the card around the
  // sparkline already announces the headline number to screen readers.
  ariaLabel: PropTypes.string,
  // Full-width gradient-filled area instead of a fixed-width bare line.
  fill: PropTypes.bool,
};

export default Sparkline;
